/** Typed calls of the v1 tenant API used by the app (shapes: docs/agent/ARCHITECTURE.md, «Ответы API»). */
import type { PassEvent } from '../domain/passEvents';
import type { RequestItem } from '../domain/requests';
import type { AddressRef, Pass, Template } from '../domain/passes';
import type { Session } from './session';

export interface ObjectInfo {
  id: number;
  name: string;
  phone: string | null;
  settings?: Record<string, unknown>;
}

export interface Address extends AddressRef {
  isBlocked: boolean;
  /** Non-zero when the resident uses the address under a power of attorney. */
  confidanceId: number;
  object: { id: number; name: string };
}

export interface Banner {
  id: number;
  bgImageUrl: string;
  bgImageActionLink: string | null;
}

interface Page<T> {
  collection: T[];
  pagination: { current?: number; next?: number | null; last?: number; total?: number } | unknown;
}

function query(params: Array<[string, string | number]>): string {
  const q = new URLSearchParams();
  for (const [k, v] of params) q.append(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const getObjects = (s: Session) => s.request<ObjectInfo[]>('/v1/profile/objects');

export const getObjectAddresses = (s: Session, objectId: number) =>
  s.request<Address[]>(`/v1/profile/object/${objectId}/addresses`);

export async function getTemplates(s: Session, addressId?: number): Promise<Template[]> {
  const page = await s.request<Page<Template>>(`/v1/templates${query(addressId ? [['filter[addressId]', addressId]] : [])}`);
  return page.collection;
}

/** `addressIds`: the passes of one object — the server ignores filter[objectId] but takes a list of addresses. */
export async function getPasses(s: Session, filter: { statuses?: number[]; addressId?: number; addressIds?: number[] } = {}): Promise<Pass[]> {
  const params: Array<[string, number]> = (filter.statuses ?? []).map((st) => ['filter[status][]', st]);
  if (filter.addressId) params.push(['filter[addressId]', filter.addressId]);
  for (const id of filter.addressIds ?? []) params.push(['filter[addressId][]', id]);
  const page = await s.request<Page<Pass>>(`/v1/passes${query(params)}`);
  return page.collection;
}

export const getNewMessagesCount = (s: Session) => s.request<number>('/v1/system-messages/new-count');

export const getBanners = (s: Session, objectId: number) => s.request<Banner[]>(`/v1/banners${query([['objectId', objectId]])}`);

export interface PassOption {
  id: number;
  name: string;
}

export interface ObjectDetails extends ObjectInfo {
  settings: Record<string, unknown>;
  options: PassOption[];
  vehicleTypes: Array<{ id: number; name: string }>;
}

export interface VehicleModel {
  id: number;
  title: string;
}

/** Body of POST /v1/passes (v1 PassController/PassValidator). Dates: naive Moscow "Y-m-d H:i:s". */
export interface NewPass {
  addressId: number;
  durationType: number;
  guestType: number;
  startsAt?: string;
  expiresAt?: string;
  guestData: { plateNumber?: string; modelId?: number; vehicleType?: number; name?: string };
  comment?: string;
  options?: number[];
}

export const getObject = (s: Session, objectId: number) => s.request<ObjectDetails>(`/v1/profile/object/${objectId}`);

export const getVehicleModels = (s: Session) => s.request<VehicleModel[]>('/v1/vehicle-models');

export const createPass = (s: Session, body: NewPass) => s.request<Pass>('/v1/passes', { method: 'POST', body });

/** Same data as a pass, sent to the managing company for approval (setting value 2). */
export const createPassRequest = (s: Session, body: NewPass) =>
  s.request<{ id: number }>('/v1/requests', { method: 'POST', body: { type: 2, requestData: body } });

export const createTemplate = (
  s: Session,
  body: Pick<NewPass, 'addressId' | 'durationType' | 'guestType' | 'guestData' | 'comment'> & { name: string },
) => s.request<Template>('/v1/templates', { method: 'POST', body });

/** POST /v1/passes/invite: no guest data, the guest fills it at inviteLink (spec §6.4). */
export interface NewInvite {
  addressId: number;
  durationType: number;
  startsAt?: string;
  expiresAt?: string;
  comment?: string;
}

export interface Invite {
  id: number;
  status: number;
  inviteLink: string | null;
}

export const createInvite = (s: Session, body: NewInvite) => s.request<Invite>('/v1/passes/invite', { method: 'POST', body });

export interface PassesQuery {
  statuses?: number[];
  addressId?: number;
  query?: string;
  fromConfidant?: boolean;
  guestType?: number;
  /** 1 one-time, 2 temporary, 3 permanent. */
  durationType?: number;
  page?: number;
}

/** One page (20 passes, server-fixed) of «Пропуска»; nextPage is null on the last page. */
export async function getPassesPage(s: Session, q: PassesQuery): Promise<{ items: Pass[]; nextPage: number | null; total: number }> {
  const params: Array<[string, string | number]> = (q.statuses ?? []).map((st) => ['filter[status][]', st]);
  if (q.addressId) params.push(['filter[addressId]', q.addressId]);
  if (q.guestType) params.push(['filter[guestType]', q.guestType]);
  // v1 filters durationType and status with InListFilter, which takes only a list: a scalar is a 400 (#4216, «Мои машины»).
  if (q.durationType) params.push(['filter[durationType][]', q.durationType]);
  if (q.query?.trim()) params.push(['query', q.query.trim()]);
  if (q.fromConfidant) params.push(['filter[fromConfidant]', 1]);
  if (q.page && q.page > 1) params.push(['page', q.page]);
  const page = await s.request<Page<Pass>>(`/v1/passes${query(params)}`);
  const p = (page.pagination ?? {}) as { next?: number | null; total?: number };
  return { items: page.collection, nextPage: p.next ?? null, total: p.total ?? page.collection.length };
}

export const getPass = (s: Session, id: number) => s.request<Pass & { sharedToken?: string | null; inviteLink?: string | null; comment?: string }>(`/v1/passes/${id}`);

const MAX_EVENT_PAGES = 5;

/** Events of a pass, newest first; follows pages so "was the guest ever inside" is answered from the whole history. */
export async function getPassEvents(s: Session, id: number): Promise<PassEvent[]> {
  const events: PassEvent[] = [];
  for (let page = 1; page <= MAX_EVENT_PAGES; page++) {
    const res = await s.request<Page<PassEvent>>(`/v1/passes/${id}/events${page > 1 ? `?page=${page}` : ''}`);
    events.push(...res.collection);
    const next = (res.pagination as { next?: number | null } | undefined)?.next;
    if (!next) break;
  }
  return events;
}

/** Guest access (QR, PIN, access code, Wallet). Never request it with `chs`: that marks the guest as inside. */
export interface PassAccess {
  status: number;
  qrImage: string | null;
  pin: string | null;
  intCode: number | null;
  accessCode: string | null;
  linkOsmi: { linkIos?: string | null; linkAndroid?: string | null } | null;
}

export const getPassAccess = (s: Session, sharedToken: string) =>
  s.request<PassAccess>(`/v1/passes/invite/${encodeURIComponent(sharedToken)}`);

/** Close a pass (status → 60) or cancel an invite (20 → 25). Inside → UPDATE_INSIDE_PASS. */
export const closePass = (s: Session, id: number) => s.request<Pass>(`/v1/passes/${id}`, { method: 'DELETE' });

/** One page (10, server-fixed) of the resident's requests for an object, newest first. */
export async function getRequestsPage(
  s: Session,
  q: { objectId: number; type?: number; page?: number },
): Promise<{ items: RequestItem[]; nextPage: number | null }> {
  const params: Array<[string, number]> = [['filter[objectId]', q.objectId]];
  if (q.type) params.push(['filter[type]', q.type]);
  if (q.page && q.page > 1) params.push(['page', q.page]);
  const page = await s.request<Page<RequestItem>>(`/v1/requests${query(params)}`);
  return { items: page.collection, nextPage: (page.pagination as { next?: number | null } | undefined)?.next ?? null };
}

export const getRequest = (s: Session, id: number) => s.request<RequestItem>(`/v1/requests/${id}`);

export interface RequestMessage {
  id: number;
  text: string;
  createdAt: string;
  /** `tenant` — the resident, `user` — the managing company. */
  sender: { id: number; name: string; type: string } | null;
  /** On the resident's own messages: the managing company has read it. */
  isRead: boolean;
}

/** Latest 20 messages, newest first. */
export async function getRequestMessages(s: Session, id: number): Promise<RequestMessage[]> {
  const page = await s.request<Page<RequestMessage>>(`/v1/requests/${id}/messages`);
  return page.collection;
}

export const sendRequestMessage = (s: Session, id: number, text: string) =>
  s.request<RequestMessage>(`/v1/requests/${id}/messages`, { method: 'POST', body: { text } });

/** Withdraw a request under review; the server marks it rejected «Запрос отклонен собственником». */
export const withdrawRequest = (s: Session, id: number) => s.request<null>(`/v1/requests/${id}`, { method: 'DELETE' });

export const createMessageToMC = (s: Session, data: { objectId: number; title: string; message: string }) =>
  s.request<RequestItem>('/v1/requests', { method: 'POST', body: { type: 4, requestData: data } });

export interface Profile {
  id: number;
  firstName: string;
  lastName: string | null;
  middleName: string | null;
  phone: string;
  email: string | null;
}

export const getProfile = (s: Session) => s.request<Profile>('/v1/profile');

/** Names are validated as alpha_dash by the server: no spaces (src/domain/people.ts). */
export const updateProfile = (
  s: Session,
  body: { firstName: string; lastName: string | null; middleName: string | null; email?: string | null },
) => s.request<Profile>('/v1/profile', { method: 'PATCH', body });

export const deleteTemplate = (s: Session, id: number) => s.request<null>(`/v1/templates/${id}`, { method: 'DELETE' });

/** All addresses of the resident across objects, including ones held by power of attorney (confidanceId ≠ 0). */
export const getAllAddresses = (s: Session) => s.request<Address[]>('/v1/profile/addresses');

export interface Confidance {
  id: number;
  address: AddressRef;
  object: { id: number; name: string };
  confidant: { firstName: string; lastName: string | null; middleName: string | null; phone: string };
  startsAt: string;
  expiresAt: string;
  /** 1 waits for the managing company, 2 active, 3 closed, 4 starts later. */
  status: number;
}

export interface NewConfidance {
  addressId: number;
  startsAt: string;
  expiresAt: string;
  confidant: { phone: string; firstName: string; lastName: string | null; middleName: string | null };
}

/** Powers of attorney issued by the resident (first page, newest first). */
export async function getConfidances(s: Session): Promise<Confidance[]> {
  const page = await s.request<Page<Confidance>>('/v1/confidances');
  return page.collection;
}

export const createConfidance = (s: Session, body: NewConfidance) => s.request<Confidance>('/v1/confidances', { method: 'POST', body });

/** When the object allows trusted persons only through the managing company. */
export const createConfidanceRequest = (s: Session, body: NewConfidance) =>
  s.request<RequestItem>('/v1/requests', { method: 'POST', body: { type: 3, requestData: body } });

export const closeConfidance = (s: Session, id: number) => s.request<Confidance>(`/v1/confidances/${id}/close`, { method: 'POST' });

export interface SystemMessage {
  id: number;
  title: string;
  text: string;
  createdAt: string;
  systemName: string;
  new: boolean;
}

/** All notifications at once (the endpoint is not paginated). */
export async function getSystemMessages(s: Session): Promise<SystemMessage[]> {
  const page = await s.request<Page<SystemMessage> | SystemMessage[]>('/v1/system-messages');
  return Array.isArray(page) ? page : page.collection;
}

/** Marking as read is a GET in v1. */
export const markSystemMessageRead = (s: Session, id: number) => s.request<unknown>(`/v1/system-messages/${id}/read`);

export interface FeedEvent {
  subjectType: string;
  /** v1 formats it for people already: «8 октября 2026 в 10:00». */
  happenedAt: string;
  title: string;
  message: string;
  /** For a pass: the pass (id, status, title) — the event opens its card. */
  subject?: { id?: number } | null;
}

/** Activity log of the resident's passes, requests and powers of attorney (not news). */
export async function getFeed(s: Session): Promise<FeedEvent[]> {
  const page = await s.request<Page<FeedEvent>>('/v1/feed');
  return page.collection;
}

/** Ask the managing company of object `objectId` to add the resident at `address` (request type 1). */
export const createJoinRequest = (s: Session, data: { objectId: number; address: string }) =>
  s.request<RequestItem>('/v1/requests', { method: 'POST', body: { type: 1, requestData: data } });

/** Unbinds the resident from every address and deletes the account. v1 reports failure as body:false. */
export async function deleteAccount(s: Session): Promise<void> {
  const result = await s.request<unknown>('/v1/auth/delete', { method: 'POST' });
  if (result === false) throw new Error('Не удалось удалить аккаунт');
}
